<template>
  <page-header title="User" :identifier="userId">
    <template v-if="isLoaded">
      <button class="btn-outline-primary ml-auto h-10 mr-2" @click="toggleEnableDisableUser">
        <template v-if="user.deletedAt">
          <EyeIcon class="size-5 -ml-1 mr-2" />
          Enable user
        </template>
        <template v-else>
          <EyeSlashIcon class="size-5 -ml-1 mr-2" />
          Disable user
        </template>
      </button>
      <button class="btn-outline-primary ml-auto h-10 mr-2" @click="addStorage">
        <CircleStackIcon class="size-5 -ml-1 mr-2" />
        Add storage
      </button>
      <button class="btn-outline-primary ml-auto h-10 mr-2" @click="addTime">
        <ClockIcon class="size-5 -ml-1 mr-2" />
        Add 10 hours
      </button>
      <button class="btn-outline-primary ml-auto h-10 mr-2" @click="removeTime">
        <NoSymbolIcon class="size-5 -ml-1 mr-2" />
        Remove time
      </button>
      <button class="btn-outline-primary ml-auto h-10 mr-2" @click="setTime">
        <ClockIcon class="size-5 -ml-1 mr-2" />
        Set time (minutes)
      </button>
      <button class="btn-primary ml-auto h-10" :class="{'disabled': isSaving || !isDataChanged}" @click="save">
        <CircleStackIcon class="size-5 -ml-1 mr-2" />
        Save
      </button>
    </template>
  </page-header>

  <pre v-if="false">
      {{ JSON.stringify(user, null, 2) }}
    </pre>

  <template v-if="isLoaded">
    <user-form ref="userFormRef" :user="user" />

    <template v-if="userId">
      <h2 class="text-xl font-semibold text-gray-700 border-b-2 border-primary-500 pb-2 mb-4 mt-6">
        Onboarding
        <span v-if="user.isOnboarded" class="bg-green-100 text-green-800 text-sm font-medium me-2 px-2.5 py-0.5 rounded-full ml-4">
          Completed
        </span>
        <span v-else class="bg-red-100 text-red-800 text-sm font-medium me-2 px-2.5 py-0.5 rounded-full ml-4">
          In Progress
        </span>
      </h2>
      <div class="grid grid-cols-1 gap-2 text-sm">
        <div v-for="(item, index) in user.Onboarding?.info" :key="index" class="flex">
          <span class="font-bold text-gray-600 w-32">{{ item.question }}:</span>
          {{ item.answers?.join(', ') || 'Not specified' }}
        </div>
      </div>

      <h2 class="text-xl font-semibold text-gray-700 border-b-2 border-primary-500 pb-2 mb-4 mt-6">
        User Information
      </h2>
      <div class="grid grid-cols-1 gap-2 text-sm">
        <div class="flex">
          <span class="font-bold text-gray-600 w-32">Available hours:</span>
          <user-can-run-programs :row="user" />
        </div>
        <div class="flex">
          <span class="font-bold text-gray-600 w-32">Storage quota:</span>
          <CircleStackIcon class="size-4 text-green-800 mr-1" />
          {{ formatBytes(user.storageQuota) }}
        </div>
        <div class="flex">
          <span class="font-bold text-gray-600 w-32">Storage usage:</span>
          <CircleStackIcon class="size-4 text-green-800 mr-1" />
          {{ formatBytes(user.storageUsage) }}
        </div>
        <div class="flex">
          <span class="font-bold text-gray-600 w-32">Last activity:</span>
          <CalendarIcon class="size-4 text-green-800 mr-1" />
          {{ user.lastActivityAt ? formatDate(user.lastActivityAt) : '-' }}
        </div>
      </div>

      <h2 class="text-xl font-semibold text-gray-700 border-b-2 border-primary-500 pb-2 mb-4 mt-6">
        Subscription History
      </h2>
      <div v-if="user.SubscriptionHistory?.length" class="space-y-4">
        <div v-for="(item, index) in user.SubscriptionHistory" :key="index" class="p-4 border rounded-lg bg-gray-50">
          <div class="text-lg font-medium flex items-center">
            <DocumentTextIcon class="size-5 -ml-1 mr-2" /> Type: <span class="capitalize">{{ subscriptionTypeLabel(item.type) }}</span>
          </div>
          <div class="text-gray-600 flex items-center">
            <CalendarIcon class="size-5 -ml-1 mr-2" /> Created: {{ formatDate(item.createdAt) }}
          </div>
          <div class="mt-2 text-gray-700">
            <div class="font-medium flex items-center">
              <BookmarkIcon class="size-5 -ml-1 mr-2" /> Details:
            </div>
            <ul class="ml-4 list-disc text-sm space-y-1">
              <li class="flex items-center">
                <TagIcon class="size-5 -ml-1 mr-2 shrink-0" /> Source: {{ item.details?.source || 'Unknown' }}
              </li>
              <li v-if="item.details?.product" class="flex items-center">
                <TagIcon class="size-5 -ml-1 mr-2 shrink-0" /> Product: {{ subscriptionTypeLabel(item.details.product) }}
              </li>
              <li v-if="item.details?.setMinutes != null" class="flex items-center">
                <ClockIcon class="size-5 -ml-1 mr-2 shrink-0" /> Time set: {{ item.details.setMinutes }} min
                <span v-if="item.details.secondsAdded != null && item.details.secondsAdded !== 0" class="text-gray-500 ml-1">
                  ({{ item.details.secondsAdded > 0 ? '+' : '' }}{{ formatTimeSigned(item.details.secondsAdded) }})
                </span>
              </li>
              <li v-else-if="item.details?.secondsAdded != null && item.details.secondsAdded !== 0" class="flex items-center">
                <ClockIcon class="size-5 -ml-1 mr-2 shrink-0" /> Time added: {{ formatTimeSigned(item.details.secondsAdded) }}
              </li>
              <li v-if="item.details?.isSubscription" class="flex items-center">
                <ClockIcon class="size-5 -ml-1 mr-2 shrink-0" /> Subscription extended
              </li>
            </ul>
          </div>
        </div>
      </div>
      <p v-else class="text-gray-500">
        No subscription history
      </p>
    </template>
  </template>

  <div v-else>
    Loading...
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, toRaw } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import PageHeader from '../components/PageHeader.vue';
import UserForm from '../components/UserForm.vue';
import UserCanRunPrograms from '../components/UserCanRunPrograms.vue';
import {
  CircleStackIcon,
  EyeIcon,
  EyeSlashIcon,
  ClockIcon,
  BookmarkIcon,
  CalendarIcon,
  DocumentTextIcon,
  TagIcon,
  NoSymbolIcon
} from '@heroicons/vue/24/outline';
import { useFetch } from '@vueuse/core';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import hashSum from 'hash-sum';
import { formatBytes } from '../utils/formatters';
import { formatDate } from '../utils/formatters';

const props = defineProps({
  userId: {
    type: String,
    default: null
  }
});

const toast = useToast();
const userFormRef = ref(null);
const isLoaded = ref(false);
const isSaving = ref(false);
const user = ref({});
const userHashSum = ref(null);

const isDataChanged = computed(() => userHashSum.value !== hashSum(user.value));

if (props.userId) {
  load();
} else {
  isLoaded.value = true;
}

async function load() {
  const { data } = await useFetch(`${apiDomain}/api/user/${props.userId}`).get().json();
  userHashSum.value = hashSum(data.value);
  user.value = data.value;
  isLoaded.value = true;
}

async function save() {
  isSaving.value = true;

  await userFormRef.value.onValidate();
  if (userFormRef.value.getResult().$invalid) {
    isSaving.value = false;
    return;
  }

  const { data, error } = await useFetch(`${apiDomain}/api/user`).post(toRaw(user.value)).json();

  isSaving.value = false;

  if (error.value) {
    console.log(error.value, data.value);
    toast.error(error.value);
  }

  if (data.value) {
    userHashSum.value = hashSum(data.value);
    user.value = data.value;
    toast.success('User saved successfully');
  }
}

const SUBSCRIPTION_TYPE_LABELS = {
  'flex-start': 'Flex Start',
  'monthly': 'Monthly',
  'semiannual': 'Semiannual',
  'annual': 'Annual',
  'manual': 'Manual',
  'trial': 'Trial',
  'hourly': 'Hourly',
  'addon-10h': 'Addon 10h',
  'addon-20h': 'Addon 20h'
};

function subscriptionTypeLabel(type) {
  return (type && SUBSCRIPTION_TYPE_LABELS[type]) || type || '—';
}

function formatTimeSigned(seconds) {
  if (seconds == null) return '—';
  const h = (seconds / 3600).toFixed(2);
  return seconds >= 0 ? `${h} hours` : `−${Math.abs(Number(h))} hours`;
}

async function toggleEnableDisableUser() {
  const direction = user.value.deletedAt ? 'enable' : 'disable';
  const { data } = await useFetch(`${apiDomain}/api/user/${direction}/${user.value.id}`).get().json();

  if (data.value?.success) {
    load();
    toast.success(`User ${direction}d successfully`);
    return;
  }

  toast.error(`Error ${direction} user`);
}

async function addTime() {
  const { data } = await useFetch(`${apiDomain}/api/user/add-time/${user.value.id}`).get().json();

  if (data.value?.success) {
    load();
    toast.success('User recieved +10 hours');
    return;
  }

  toast.error('Error adding +10 hours');
}

async function removeTime() {
  const { data } = await useFetch(`${apiDomain}/api/user/remove-time/${user.value.id}`).get().json();

  if (data.value?.success) {
    load();
    toast.success('User time completely removed');
    return;
  }

  toast.error('Error removing time');
}

async function setTime() {
  const input = window.prompt('Set time (minutes):', String(Math.round((user.value?.timeLeftSeconds ?? 0) / 60)));
  if (input === null) return;

  const minutes = Number(input);
  if (!Number.isFinite(minutes) || minutes < 0) {
    toast.error('Enter a non‑negative number of minutes');
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/user/set-time/${user.value.id}`).post({ minutes }).json();

  if (data.value?.success) {
    load();
    toast.success(`Time set to ${minutes} minutes`);
    return;
  }

  toast.error('Error setting time');
}

async function addStorage() {
  const input = window.prompt('Add storage (GB):', '10');
  if (input === null) return;

  const gb = Number(input);
  if (!Number.isFinite(gb) || gb <= 0) {
    toast.error('Enter a positive number of GB');
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/user/add-storage/${user.value.id}`).post({ gb }).json();

  if (data.value?.success) {
    load();
    toast.success(`Added ${gb} GB of storage`);
    return;
  }

  toast.error('Error adding storage');
}

function handleBeforeUnload(e) {
  if (!isDataChanged.value || !Object.keys(user.value).length) {
    return;
  }
  e.preventDefault();
  e.returnValue = '';
}

onBeforeRouteLeave(async (to, from, next) => {
  if (!isDataChanged.value || !Object.keys(user.value).length) {
    return next();
  }

  const confirm = await Modal.confirm({
    title: 'Unsaved changes',
    body: 'You have unsaved changes. <br/> If you leave this page, your changes will be lost. <br/> Are you sure you want to exit without saving?',
    okButton: 'Leave',
    iconColor: 'primary'
  });

  return confirm ? next() : next(false);
});

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>
